import { FeatureFlagTypes } from '@generated/graphql';
import { CountryCodes } from '@onevesthq/ov-enums';
import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { useOrganizationFeatureFlag } from '../../../../../../hooks/useOrganizationFeatureFlag';
import { isParticipantNumberLengthValid } from './depositExternalTransferRules';

type UseDepositExternalTransferProp = {
  deposit: TransferContextData;
};

const getOriginOfAccount = (accountType?: string) => {
  if (!accountType || accountType === '') return undefined;
  if (accountType.includes('USA_')) return CountryCodes.US;
  if (accountType.includes('UK_')) return CountryCodes.GB;
  return CountryCodes.CA;
};

const isParticipantNumberEnabled = ({
  usTransferFlowEnabled,
  accountType,
}: {
  usTransferFlowEnabled: boolean;
  accountType?: string;
}) => usTransferFlowEnabled && (accountType?.includes('USA_') ?? false);

export const useDepositExternalTransfer = ({
  deposit,
}: UseDepositExternalTransferProp) => {
  const usTransferFlowEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.UsTransferFlow,
  );
  const enableInKind = useOrganizationFeatureFlag(
    FeatureFlagTypes.InKindTransfers,
  );
  const enabledManualInstitutionCreation = useOrganizationFeatureFlag(
    FeatureFlagTypes.AddInstitutionsManuallyInUce,
  );

  const participantNumberEnabled = isParticipantNumberEnabled({
    usTransferFlowEnabled,
    accountType: deposit.subAccount?.account?.type,
  });
  const originAccountType = getOriginOfAccount(
    deposit.subAccount?.account?.type,
  );
  const hasInvalidParticipantNumber =
    participantNumberEnabled &&
    deposit?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER &&
    !isParticipantNumberLengthValid(deposit?.participantNumber ?? '');

  return {
    usTransferFlowEnabled,
    enableInKind,
    enabledManualInstitutionCreation,
    participantNumberEnabled,
    originAccountType,
    hasInvalidParticipantNumber,
  };
};
